import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import api from "../utils/api";
import { showGlobalToast } from "../context/ToastContext";
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import PageHeader from "../components/PageHeader";
import { UserPlus, Trash2, Shield, Calendar, Building2, Search, Edit2, X, Check, CheckCircle, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { totalMembersIcon, systemAdminsIcon, activeAgentsIcon, assignedUsersIcon } from "../components/icons";

const CustomSelect = ({ value, options, onChange, label, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-5 py-3 bg-gray-50 border border-transparent rounded-2xl transition-all duration-300 font-bold text-xs ${isOpen ? 'bg-white border-gray-900 ring-4 ring-gray-900/5 shadow-sm' : 'hover:bg-gray-100'
          }`}
      >
        <span className="text-gray-900 truncate">{selectedOption?.label || "Select Option"}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-gray-900' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-slideIn">
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`px-5 py-3 text-xs font-bold transition-colors cursor-pointer flex items-center justify-between ${value === opt.value
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
              >
                {opt.label}
                {value === opt.value && <Check size={14} className="text-white" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MultiSelect = ({ value = [], options, onChange, label, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const selectedLabels = options
    .filter(opt => value.includes(opt.value))
    .map(opt => opt.label);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-5 py-3 bg-gray-50 border border-transparent rounded-2xl transition-all duration-300 font-bold text-xs ${isOpen ? 'bg-white border-gray-900 ring-4 ring-gray-900/5 shadow-sm' : 'hover:bg-gray-100'
          }`}
      >
        <span className="text-gray-900 truncate">
          {selectedLabels.length > 0 ? selectedLabels.join(", ") : "No Bots Assigned"}
        </span>
        <div className="flex items-center gap-2">
          {selectedLabels.length > 0 && (
            <span className="bg-gray-900 text-white text-[10px] px-2 py-0.5 rounded-full">
              {selectedLabels.length}
            </span>
          )}
          <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-gray-900' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-slideIn">
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => toggleOption(opt.value)}
                className={`px-5 py-3 text-xs font-bold transition-colors cursor-pointer flex items-center justify-between ${value.includes(opt.value)
                  ? "bg-gray-50 text-gray-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${value.includes(opt.value) ? "bg-gray-900 border-gray-900" : "bg-white border-gray-200"
                    }`}>
                    {value.includes(opt.value) && <Check size={12} className="text-white" />}
                  </div>
                  {opt.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const UserManagement = ({ agents = [] }) => {
  const isTeamsRoles = true;

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 10;

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "human_agent",
    assigned_ai_agents: []
  });

  const [editFormData, setEditFormData] = useState({
    id: null,
    username: "",
    email: "",
    password: "",
    role: "human_agent",
    assigned_ai_agents: [],
    is_active: true
  });

  const [billingInfo, setBillingInfo] = useState({
    maxUsers: 3,
    planName: "Starter"
  });

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.request("/users");
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const loadBillingInfo = async () => {
      try {
        const userStr = sessionStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;
        const tenantId = user?.tenant_id || user?.username || "admin";

        const subRes = await api.request(`/api/subscription/${tenantId}`);
        let maxUsers = 0; // Default fallback to 0 when no plan is active
        let planName = "No Plan";
        if (subRes && subRes.success && subRes.data) {
          const planId = subRes.data.plan_id.toLowerCase();
          planName = subRes.data.plan_name;
          const PLAN_PRESETS = {
            free_trial: 1,
            starter: 3,
            growth: 5,
            professional: 7,
            enterprise: 999
          };
          maxUsers = (PLAN_PRESETS[planId] || 0) + (subRes.data.extra_human_agents || 0);
        }
        setBillingInfo({ maxUsers, planName });
      } catch (err) {
        console.error("Failed to load billing limits for UserManagement page:", err);
      }
    };

    loadBillingInfo();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.request("/users", {
        method: "POST",
        body: JSON.stringify(formData)
      });

      showGlobalToast(`Identity Provisioned Successfully for ${formData.username}`, "success");

      setShowAddModal(false);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "human_agent",
        assigned_ai_agents: []
      });
      fetchUsers();
    } catch (error) {
      console.error("Creation Failed:", error);
    }
  };

  const handleEditClick = (user) => {
    setEditFormData({
      id: user.id,
      username: user.username,
      email: user.email,
      password: "", // Leave blank to preserve password
      role: user.role,
      assigned_ai_agents: user.assigned_ai_agents || [],
      is_active: user.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const updates = {
        username: editFormData.username,
        email: editFormData.email,
        role: editFormData.role,
        assigned_ai_agents: editFormData.assigned_ai_agents,
        is_active: editFormData.is_active
      };

      if (editFormData.password) {
        updates.password = editFormData.password;
      }

      await api.request(`/users/${editFormData.id}`, {
        method: "PATCH",
        body: JSON.stringify(updates)
      });

      showGlobalToast("Identity & Access Protocol Updated Successfully", "success");
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Update Failed:", error);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await api.request(`/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(updates)
      });
      showGlobalToast("Identity Status Updated", "success");
      fetchUsers();
    } catch (error) {
      console.error("Update Failed:", error);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await api.request(`/users/${userToDelete.id}`, {
        method: "DELETE"
      });
      showGlobalToast("Identity Deleted Successfully", "success");
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error("Deletion Failed:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredUsers = users
    .filter(user =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Admin always first
      if (a.role === 'admin' && b.role !== 'admin') return -1;
      if (a.role !== 'admin' && b.role === 'admin') return 1;
      // Then sort by username
      return a.username.localeCompare(b.username);
    });

  const agentOptions = agents.map(p => ({ value: p.id, label: p.name }));
  const roleOptions = [
    { value: "user", label: "User" },
    { value: "human_agent", label: "Human Agent" }
  ];

  // Pagination Logic
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn">
      <div className="max-w-6xl mx-auto">
        <PageHeader
          title={isTeamsRoles ? "Teams & Roles" : "Contacts"}
          description={isTeamsRoles ? "Provision operator identities, configure roles, and orchestrate access control for your system." : "Manage your operator contacts, provision credentials, and allocate bot permissions."}
          actions={
            <div className="flex items-center gap-4">
              <span className="text-xs font-bold text-gray-500">
                Limit: {users.length} / {billingInfo.maxUsers} Users
              </span>
              <button
                onClick={() => {
                  if (users.length >= billingInfo.maxUsers) {
                    showGlobalToast(`You have reached the maximum limit of ${billingInfo.maxUsers} users allowed on your current plan. Please upgrade to unlock more slots.`, "error");
                    return;
                  }
                  setShowAddModal(true);
                }}
                className="flex items-center justify-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-2xl hover:bg-gray-800 transition-all font-black text-sm uppercase shadow-xl shadow-gray-200"
              >
                <UserPlus size={18} />
                {isTeamsRoles ? "Provision New Member" : "Add Contact"}
              </button>
            </div>
          }
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">

          {/* Total Members */}
          <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
            <div className="flex flex-col justify-between min-h-[102px]">
              <span className="text-[10px] font-black text-black uppercase">{isTeamsRoles ? "Total Members" : "Total Contacts"}</span>
              <div>
                <span className="text-3xl font-black text-gray-900 tracking-tighter">{users.length}</span>
                <p className="text-[10px] font-semibold text-gray-400 mt-1">provisioned slots</p>
              </div>
            </div>
            <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
              <img src={totalMembersIcon} className="w-full h-full object-cover scale-110" alt="" />
            </div>
          </div>

          {/* System Admins */}
          <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
            <div className="flex flex-col justify-between min-h-[102px]">
              <span className="text-[10px] font-black text-black uppercase">System Admins</span>
              <div>
                <span className="text-3xl font-black text-gray-900 tracking-tighter">{users.filter(u => u.role === 'admin').length}</span>
                <p className="text-[10px] font-semibold text-gray-400 mt-1">full console access</p>
              </div>
            </div>
            <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
              <img src={systemAdminsIcon} className="w-full h-full object-cover scale-110" alt="" />
            </div>
          </div>

          {/* Active Human Agents */}
          <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
            <div className="flex flex-col justify-between min-h-[102px]">
              <span className="text-[10px] font-black text-black uppercase">Active Human Agents</span>
              <div>
                <span className="text-3xl font-black text-gray-900 tracking-tighter">{users.filter(u => u.role === 'human_agent' || u.role === 'admin').length}</span>
                <p className="text-[10px] font-semibold text-gray-400 mt-1">live conversation flow</p>
              </div>
            </div>
            <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
              <img src={activeAgentsIcon} className="w-full h-full object-cover scale-110" alt="" />
            </div>
          </div>

          {/* Assigned Users */}
          <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
            <div className="flex flex-col justify-between min-h-[102px]">
              <span className="text-[10px] font-black text-black uppercase">Assigned Users</span>
              <div>
                <span className="text-3xl font-black text-gray-900 tracking-tighter">{users.filter(u => u.role === 'user').length}</span>
                <p className="text-[10px] font-semibold text-gray-400 mt-1">configured end users</p>
              </div>
            </div>
            <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
              <img src={assignedUsersIcon} className="w-full h-full object-cover scale-110" alt="" />
            </div>
          </div>

        </div>

        {/* Users Table */}
        <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideIn">
          <div className="p-8 border-b border-gray-50 bg-gray-50/30 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder={isTeamsRoles ? "Search by username, email, or role..." : "Search contacts..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-6 py-3 bg-white border border-gray-100 rounded-2xl text-xs font-bold tracking-tight focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all shadow-sm"
              />
            </div>
          </div>

          <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
            <table className="w-full text-left">
              <thead className="bg-gray-50/50 sticky top-0 z-10 shadow-sm backdrop-blur-md">
                <tr>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Identity</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Access Level</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Assigned Bots</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Created At</th>
                  <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Protocol</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="px-8 py-24 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin"></div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{isTeamsRoles ? "Syncing Teams Data..." : "Syncing Contacts Data..."}</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-8 py-24 text-center">
                      <div className="w-20 h-20 bg-gray-50 rounded-[28px] border border-gray-100 flex items-center justify-center mb-6 mx-auto">
                        <Search size={32} className="text-gray-300" />
                      </div>
                      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2 tracking-tight">No Members Found</h3>
                      <p className="text-gray-400 max-w-xs mx-auto text-sm">Adjust your search or provision a new team member.</p>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-all group">
                      <td className="px-6 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-900 font-black shadow-sm group-hover:bg-white group-hover:scale-110 transition-all">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-black text-gray-900 tracking-tight">{user.username}</span>
                            <span className="text-[10px] text-gray-400 font-bold tracking-tight lowercase">
                              {user.email}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        {user.role === 'admin' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-sm bg-gray-900">
                            <Shield size={12} className="text-white" />
                            Admin
                          </span>
                        ) : (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${user.role === 'human_agent' ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                            'bg-orange-50 text-orange-700 border border-orange-100'
                            }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${user.role === 'human_agent' ? 'bg-blue-500' :
                              'bg-orange-500'
                              }`}></div>
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-6 min-w-[200px]">
                        {user.role === 'admin' ? (
                          <span className="text-[10px] font-black text-[#83bbe1] uppercase tracking-widest flex items-center gap-2">
                            <Building2 size={12} />
                            Full System Access (ALL)
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {user.assigned_ai_agents && user.assigned_ai_agents.length > 0 ? (
                              user.assigned_ai_agents.map(botId => {
                                const bot = agents.find(p => p.id === botId);
                                return (
                                  <span key={botId} className="px-2 py-0.5 bg-gray-100 rounded-lg text-[9px] font-bold text-gray-600">
                                    {bot?.name || `Bot #${botId}`}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-[10px] font-bold text-gray-300 italic">No Bots Assigned</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-6">
                        {user.role === 'admin' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-green-50 text-green-700 border border-green-100">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                            Active
                          </span>
                        ) : (
                          <button
                            onClick={() => handleUpdateUser(user.id, { is_active: user.is_active === false ? true : false })}
                            className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white flex-shrink-0 ${user.is_active !== false ? 'border-[#83bbe1]' : 'border-gray-200'}`}
                            title={user.is_active !== false ? "Suspend User" : "Activate User"}
                          >
                            <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${user.is_active !== false ? 'left-[22px] bg-[#83bbe1]' : 'left-[2px] bg-gray-300'}`} />
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-6">
                        <div className="flex flex-col text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          <span className="flex items-center gap-1.5">
                            <Calendar size={12} className="text-gray-400" />
                            {(() => {
                              let dateStr = user.created_at;
                              if (dateStr && typeof dateStr === 'string') {
                                if (!dateStr.includes('T') && dateStr.includes(' ')) {
                                  dateStr = dateStr.replace(' ', 'T');
                                }
                                if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('GMT')) {
                                  dateStr = dateStr + 'Z';
                                }
                              }
                              const date = new Date(dateStr);
                              return date.toLocaleString([], {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              });
                            })()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-6 text-right">
                        <div className="flex items-center justify-end gap-2 transition-all">
                          {user.role === 'admin' && (
                            <div
                              className="flex items-center justify-center p-2.5 rounded-xl border transition-all bg-gray-50 text-gray-400 border-gray-100"
                              title="Protected System Identity"
                            >
                              <Shield size={18} />
                            </div>
                          )}
                          {user.role !== 'admin' && (
                            <>
                              <button
                                onClick={() => handleEditClick(user)}
                                className="p-2.5 text-gray-400 bg-white border border-gray-100 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 rounded-xl transition-all shadow-sm flex items-center justify-center"
                                title="Edit Member & Permissions"
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => setUserToDelete(user)}
                                className="p-2.5 text-gray-400 bg-white border border-gray-100 hover:text-red-500 hover:bg-red-50 hover:border-red-100 rounded-xl transition-all shadow-sm flex items-center justify-center"
                                title="Delete Identity"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-8 py-6 border-t border-gray-50 flex items-center justify-between bg-gray-50/30">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Showing <span className="text-gray-900">{(currentPage - 1) * USERS_PER_PAGE + 1}</span> to <span className="text-gray-900">{Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)}</span> of <span className="text-gray-900">{filteredUsers.length}</span> members
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="flex items-center gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-8 h-8 rounded-lg text-[10px] font-black transition-all ${currentPage === i + 1
                        ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Provisioning Modal */}
      {showAddModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-fadeIn">
          <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl animate-scaleIn border border-gray-100/50 overflow-hidden">
            <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20 rounded-t-[32px]">
              <div>
                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase">
                  {isTeamsRoles ? "Identity Initialization" : "New Contact Protocol"}
                </h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">{isTeamsRoles ? "Provisioning Protocol" : "Contact Setup Protocol"}</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="p-7 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Username</label>
                  <input
                    required
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="e.g. neuro_ops_01"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email</label>
                  <input
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="identity@neural-net.com"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Security Key (Password)</label>
                  <input
                    required
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min 8 characters"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all tracking-widest"
                  />
                </div>
                <CustomSelect
                  label="System Role"
                  value={formData.role}
                  options={roleOptions}
                  onChange={(val) => setFormData({ ...formData, role: val })}
                />
              </div>

              <div className="space-y-2">
                <MultiSelect
                  label="Assigned Bots (Access Control)"
                  value={formData.assigned_ai_agents}
                  options={agentOptions}
                  onChange={(val) => setFormData({ ...formData, assigned_ai_agents: val })}
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gray-900 text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl hover:bg-gray-800 transition-all shadow-xl shadow-gray-200 active:scale-95 flex items-center justify-center gap-3 mt-4"
              >
                <CheckCircle size={18} />
                {isTeamsRoles ? "Confirm Provisioning" : "Confirm Contact Creation"}
              </button>
            </form>
          </div>
        </div>
      , document.body)}

      {/* Edit Credentials Modal */}
      {showEditModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4 animate-fadeIn">
          <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl animate-scaleIn border border-gray-100/50 overflow-hidden">
            <div className="px-8 py-5 border-b border-gray-50 flex justify-between items-center bg-gray-50/20 rounded-t-[32px]">
              <div>
                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase">
                  {isTeamsRoles ? "Edit Identity Agent" : "Edit Contact Agent"}
                </h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] mt-0.5">{isTeamsRoles ? "Teams & Permissions Protocol" : "Contact Settings Protocol"}</p>
              </div>
              <button onClick={() => setShowEditModal(false)} className="p-2 text-gray-400 hover:text-gray-900 hover:bg-white rounded-xl transition-all">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleEditUserSubmit} className="p-7 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Username</label>
                  <input
                    required
                    type="text"
                    value={editFormData.username}
                    onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                    placeholder="e.g. neuro_ops_01"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Email</label>
                  <input
                    required
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    placeholder="identity@neural-net.com"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Security Key (Password)</label>
                  <input
                    type="password"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    placeholder="Leave blank to keep same"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-300 font-bold text-xs transition-all tracking-widest"
                  />
                </div>
                <CustomSelect
                  label="System Role"
                  value={editFormData.role}
                  options={roleOptions}
                  onChange={(val) => setEditFormData({ ...editFormData, role: val })}
                />
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <MultiSelect
                    label="Assigned Bots"
                    value={editFormData.assigned_ai_agents}
                    options={agentOptions}
                    onChange={(val) => setEditFormData({ ...editFormData, assigned_ai_agents: val })}
                  />
                </div>
                <CustomSelect
                  label="Status"
                  value={editFormData.is_active}
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "Suspended" }
                  ]}
                  onChange={(val) => setEditFormData({ ...editFormData, is_active: val })}
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gray-900 text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl hover:bg-gray-800 transition-all shadow-xl shadow-gray-200 active:scale-95 flex items-center justify-center gap-3 mt-4"
              >
                <CheckCircle size={18} />
                Save Changes
              </button>
            </form>
          </div>
        </div>
      , document.body)}

      <DeleteConfirmModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteUser}
        title="Delete Identity"
        message={`Are you sure you want to permanently delete the identity protocol for ${userToDelete?.username}? This action is irreversible and will revoke all access instantly.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default UserManagement;
